import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import trLocale from "@fullcalendar/core/locales/tr";
import enLocale from "@fullcalendar/core/locales/en-gb";
import type { EventClickArg, DatesSetArg } from "@fullcalendar/core";
import type { EventInput } from "@fullcalendar/core";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import InfoCard from "@/components/ui/info-card";
import { Book, Calendar, Clock, Target } from "@/components/icons";
import { useStore } from "@/store";
import { isEducationManagerSession } from "@/core/auth/utils/education-manager-session";
import { getEducationCalendarEvents } from "../services/education-calendar.api";
import type { EducationCalendarEventKind } from "../types/education-calendar-api.types";
import styles from "./EducationCalendarPage.module.scss";

const TRAINING_COLOR = "#3b82f6";
const REQUEST_COLOR = "#f59e0b";
const DEADLINE_COLOR = "#ef4444";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / MS_PER_DAY);
}

function getEventKind(ev: EventInput): EducationCalendarEventKind {
  const k = (ev.extendedProps as { kind?: EducationCalendarEventKind } | undefined)?.kind;
  return k ?? "training";
}

const EducationCalendarPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const userType = useStore((s) => s.userType);
  const currentUser = useStore((s) => s.currentUser);
  const allowed = isEducationManagerSession(userType, currentUser);

  const [events, setEvents] = useState<EventInput[]>([]);
  const [loading, setLoading] = useState(false);
  const [rangeKey, setRangeKey] = useState<string | null>(null);

  const formatDate = (d: string | Date | undefined) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleDateString(i18n.language, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return String(d);
    }
  };

  const onEventClick = (info: EventClickArg) => {
    const p = info.event.extendedProps as Record<string, string | undefined>;
    const lines: string[] = [];
    if (p.kind === "training" || !p.kind) {
      if (p.requestDeadline)
        lines.push(`Son başvuru: ${formatDate(p.requestDeadline)}`);
      if (p.approvalDue) lines.push(`Onay bitiş: ${formatDate(p.approvalDue)}`);
    }
    if (p.kind === "request_deadline" || p.kind === "request_window") {
      if (p.trainingTitle) lines.push(`Eğitim: ${p.trainingTitle}`);
      if (p.trainingStart)
        lines.push(
          `Dönem: ${formatDate(p.trainingStart)} → ${formatDate(p.trainingEnd)}`,
        );
    }
    toast.info(info.event.title, {
      description: lines.length > 0 ? lines.join("\n") : undefined,
    });
  };

  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    setRangeKey(`${arg.startStr}|${arg.endStr}`);
  }, []);

  useEffect(() => {
    if (!rangeKey || !allowed) return;
    const [from, to] = rangeKey.split("|");
    if (!from || !to) return;

    let cancelled = false;
    setLoading(true);
    getEducationCalendarEvents({ from, to })
      .then((list) => {
        if (!cancelled) setEvents(list);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setEvents([]);
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? "";
        toast.error(
          msg || t("educationCalendar.loadError"),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rangeKey, allowed, t]);

  // ---- Stats derived from current events -----------------------------------
  const now = useMemo(() => new Date(), []);

  const stats = useMemo(() => {
    const totalTrainings = events.filter((e) => getEventKind(e) === "training").length;
    const totalRequests = events.filter(
      (e) => getEventKind(e) === "request_window" || getEventKind(e) === "request_deadline",
    ).length;

    const upcoming7 = events.filter((e) => {
      if (getEventKind(e) !== "training") return false;
      const start = e.start ? new Date(e.start as string | Date) : null;
      if (!start) return false;
      const diff = daysBetween(now, start);
      return diff >= 0 && diff <= 7;
    }).length;

    const criticalDeadlines = events.filter((e) => {
      const kind = getEventKind(e);
      if (kind !== "request_deadline" && kind !== "training") return false;
      const p = e.extendedProps as { requestDeadline?: string } | undefined;
      const deadline = p?.requestDeadline ? new Date(p.requestDeadline) : null;
      if (!deadline) return false;
      const diff = daysBetween(now, deadline);
      return diff >= 0 && diff <= 3;
    }).length;

    return { totalTrainings, totalRequests, upcoming7, criticalDeadlines };
  }, [events, now]);

  const upcomingList = useMemo(() => {
    return events
      .filter((e) => getEventKind(e) === "training")
      .map((e) => ({
        id: String(e.id ?? ""),
        title: String(e.title ?? ""),
        start: e.start ? new Date(e.start as string | Date) : null,
        end: e.end ? new Date(e.end as string | Date) : null,
        deadline: (e.extendedProps as { requestDeadline?: string } | undefined)
          ?.requestDeadline
          ? new Date(
              (e.extendedProps as { requestDeadline?: string }).requestDeadline as string,
            )
          : null,
      }))
      .filter((e) => e.start && daysBetween(now, e.start) >= 0)
      .sort((a, b) => (a.start as Date).getTime() - (b.start as Date).getTime())
      .slice(0, 6);
  }, [events, now]);

  const deadlineList = useMemo(() => {
    const items: { id: string; title: string; deadline: Date; daysLeft: number }[] = [];
    for (const e of events) {
      const p = e.extendedProps as { requestDeadline?: string } | undefined;
      if (!p?.requestDeadline) continue;
      const d = new Date(p.requestDeadline);
      const daysLeft = daysBetween(now, d);
      if (daysLeft < 0 || daysLeft > 14) continue;
      items.push({
        id: String(e.id ?? Math.random()),
        title: String(e.title ?? ""),
        deadline: d,
        daysLeft,
      });
    }
    return items.sort((a, b) => a.daysLeft - b.daysLeft).slice(0, 5);
  }, [events, now]);

  const monthlyChart = useMemo(() => {
    const months: Record<number, { training: number; request: number }> = {};
    for (const e of events) {
      const start = e.start ? new Date(e.start as string | Date) : null;
      if (!start) continue;
      const m = start.getMonth();
      months[m] ??= { training: 0, request: 0 };
      const kind = getEventKind(e);
      if (kind === "training") months[m].training += 1;
      else months[m].request += 1;
    }
    const labels = Array.from({ length: 12 }, (_, i) => t(`monthsShort.${i}`));
    const trainingSeries: number[] = labels.map((_, i) => months[i]?.training ?? 0);
    const requestSeries: number[] = labels.map((_, i) => months[i]?.request ?? 0);
    return { labels, trainingSeries, requestSeries };
  }, [events]);

  const monthlyBarOptions: ApexOptions = {
    chart: { toolbar: { show: false }, fontFamily: "inherit", stacked: true },
    plotOptions: {
      bar: { borderRadius: 6, columnWidth: "55%" },
    },
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    colors: [TRAINING_COLOR, REQUEST_COLOR],
    xaxis: {
      categories: monthlyChart.labels,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: "#64748b", fontSize: "12px" } },
    },
    yaxis: {
      labels: { style: { colors: "#64748b", fontSize: "12px" } },
    },
    grid: { borderColor: "#eef2f7", strokeDashArray: 4 },
    legend: { show: false },
    tooltip: { theme: "light" },
  };

  const kindDonutSeries = useMemo(() => {
    const training = events.filter((e) => getEventKind(e) === "training").length;
    const window = events.filter((e) => getEventKind(e) === "request_window").length;
    const deadline = events.filter((e) => getEventKind(e) === "request_deadline").length;
    return [training, window, deadline];
  }, [events]);

  const kindDonutTotal = kindDonutSeries.reduce((a, b) => a + b, 0);

  const kindDonutOptions: ApexOptions = {
    chart: {
      toolbar: { show: false },
      fontFamily: "inherit",
      sparkline: { enabled: false },
    },
    labels: [t("educationCalendar.donutLabels.training"), t("educationCalendar.donutLabels.requestPeriod"), t("educationCalendar.donutLabels.requestDeadline")],
    colors: [TRAINING_COLOR, REQUEST_COLOR, DEADLINE_COLOR],
    legend: { show: false },
    stroke: { width: 4, colors: ["#fff"] },
    dataLabels: { enabled: false },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "horizontal",
        shadeIntensity: 0.15,
        gradientToColors: ["#60a5fa", "#fbbf24", "#f87171"],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 0.9,
      },
    },
    tooltip: {
      y: {
        formatter: (val: number) => {
          const pct = kindDonutTotal > 0 ? Math.round((val / kindDonutTotal) * 100) : 0;
          return `${val} (%${pct})`;
        },
      },
    },
    plotOptions: {
      pie: {
        expandOnClick: false,
        donut: {
          size: "74%",
          labels: { show: false },
        },
      },
    },
    states: {
      hover: { filter: { type: "lighten" } },
      active: { filter: { type: "none" } },
    },
  };

  const donutLegend = useMemo(
    () => [
      { label: t("educationCalendar.donutLabels.training"), value: kindDonutSeries[0], color: TRAINING_COLOR },
      { label: t("educationCalendar.donutLabels.requestPeriod"), value: kindDonutSeries[1], color: REQUEST_COLOR },
      { label: t("educationCalendar.donutLabels.requestDeadline"), value: kindDonutSeries[2], color: DEADLINE_COLOR },
    ],
    [kindDonutSeries],
  );

  if (!allowed) {
    return <Navigate replace to="/dashboard/unauthorized" />;
  }

  return (
    <DashboardLayout>
      <Header />
      <PageHeader title={t("educationCalendar.title")} />

      <div className={styles.page}>
        {/* KPI Row */}
        <div className={styles.kpiGrid}>
          <InfoCard
            title={t("educationCalendar.stats.shownPeriodTrainings")}
            value={stats.totalTrainings.toLocaleString("tr-TR")}
            icon={<Book width={22} height={22} color="#fff" />}
            iconBgColor={TRAINING_COLOR}
            onClick={() => navigate("/dashboard/education-management")}
          />
          <InfoCard
            title={t("educationCalendar.stats.requestApplications")}
            value={stats.totalRequests.toLocaleString("tr-TR")}
            icon={<Calendar width={22} height={22} color="#fff" />}
            iconBgColor={REQUEST_COLOR}
          />
          <InfoCard
            title={t("educationCalendar.stats.upcoming7Days")}
            value={stats.upcoming7.toLocaleString("tr-TR")}
            icon={<Clock width={22} height={22} color="#fff" />}
            iconBgColor="#22c55e"
          />
          <InfoCard
            title={t("educationCalendar.stats.criticalDeadlines")}
            value={stats.criticalDeadlines.toLocaleString("tr-TR")}
            icon={<Target width={22} height={22} color="#fff" />}
            iconBgColor={DEADLINE_COLOR}
          />
        </div>

        {/* Main row: calendar + sidebar */}
        <div className={styles.mainGrid}>
          <section className={`${styles.card} ${styles.calendarCard}`}>
            <div className={styles.cardHeader}>
              <div>
                <h3>Takvim</h3>
                <p>
                  {loading
                    ? t("common.loading")
                    : t("educationCalendar.viewChangeHint")}
                </p>
              </div>
              <div className={styles.legendRow}>
                <span className={styles.legendItem}>
                  <span className={styles.swatch} style={{ background: TRAINING_COLOR }} />
                  Eğitim
                </span>
                <span className={styles.legendItem}>
                  <span className={styles.swatch} style={{ background: REQUEST_COLOR }} />
                  Talep
                </span>
                <span className={styles.legendItem}>
                  <span className={styles.swatch} style={{ background: DEADLINE_COLOR }} />
                  Son Başvuru
                </span>
              </div>
            </div>
            <div className={styles.calendarWrap}>
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: "prev,next today",
                  center: "title",
                  right: "dayGridMonth,timeGridWeek,listWeek",
                }}
                buttonText={{
                  today: t("educationCalendar.calendarLocale.today"),
                  month: "Ay",
                  week: "Hafta",
                  day: t("educationCalendar.calendarLocale.day"),
                  list: "Liste",
                  listWeek: "Liste",
                }}
                locales={[trLocale, enLocale]}
                locale={i18n.language.startsWith("tr") ? "tr" : "en-gb"}
                height="auto"
                editable={false}
                selectable={false}
                dayMaxEvents={3}
                moreLinkText={(n) => `+${n}`}
                events={events}
                eventClick={onEventClick}
                datesSet={handleDatesSet}
                navLinks
                nowIndicator
                slotMinTime="08:00:00"
                slotMaxTime="20:00:00"
              />
            </div>
          </section>

          <aside className={styles.sidebar}>
            <section className={styles.card}>
              <div className={styles.cardHeader}>
                <h3>{t("educationCalendar.upcomingTrainings.title")}</h3>
                <p>{t("educationCalendar.upcomingTrainings.subtitle")}</p>
              </div>
              {upcomingList.length === 0 ? (
                <div className={styles.emptyCell}>{t("educationCalendar.upcomingTrainings.empty")}</div>
              ) : (
                <ul className={styles.eventList}>
                  {upcomingList.map((e) => {
                    const daysLeft = e.start ? daysBetween(now, e.start) : 0;
                    return (
                      <li key={e.id} className={styles.eventItem}>
                        <div className={styles.eventDot} style={{ background: TRAINING_COLOR }} />
                        <div className={styles.eventInfo}>
                          <span className={styles.eventTitle}>{e.title}</span>
                          <span className={styles.eventMeta}>
                            {formatDate(e.start ?? undefined)}
                            {e.end && (
                              <>
                                {" → "}
                                {formatDate(e.end)}
                              </>
                            )}
                          </span>
                        </div>
                        <span className={styles.daysChip}>
                          {daysLeft === 0 ? t("educationCalendar.today") : t("educationCalendar.daysCount", { n: daysLeft })}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className={styles.card}>
              <div className={styles.cardHeader}>
                <h3>{t("educationCalendar.criticalDeadlines.title")}</h3>
                <p>{t("educationCalendar.criticalDeadlines.subtitle")}</p>
              </div>
              {deadlineList.length === 0 ? (
                <div className={styles.emptyCell}>{t("educationCalendar.criticalDeadlines.empty")}</div>
              ) : (
                <ul className={styles.eventList}>
                  {deadlineList.map((d) => {
                    const urgent = d.daysLeft <= 3;
                    return (
                      <li key={d.id} className={styles.eventItem}>
                        <div
                          className={styles.eventDot}
                          style={{ background: urgent ? DEADLINE_COLOR : REQUEST_COLOR }}
                        />
                        <div className={styles.eventInfo}>
                          <span className={styles.eventTitle}>{d.title}</span>
                          <span className={styles.eventMeta}>
                            Son tarih: {formatDate(d.deadline)}
                          </span>
                        </div>
                        <span
                          className={`${styles.daysChip} ${urgent ? styles.daysChipUrgent : ""}`}
                        >
                          {d.daysLeft === 0 ? t("educationCalendar.today") : t("educationCalendar.daysCount", { n: d.daysLeft })}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </aside>
        </div>

        {/* Charts row */}
        <div className={styles.chartsRow}>
          <section className={`${styles.card} ${styles.chartCard}`}>
            <div className={styles.cardHeader}>
              <h3>{t("educationCalendar.monthlyDistribution.title")}</h3>
              <p>{t("educationCalendar.monthlyDistribution.subtitle")}</p>
            </div>
            <div className={styles.chartBody}>
              <Chart
                options={monthlyBarOptions}
                series={[
                  { name: t("educationCalendar.monthlyChartTraining"), data: monthlyChart.trainingSeries },
                  { name: "Talep", data: monthlyChart.requestSeries },
                ]}
                type="bar"
                height={280}
              />
            </div>
          </section>

          <section className={`${styles.card} ${styles.chartCard}`}>
            <div className={styles.cardHeader}>
              <h3>Etkinlik Tipi</h3>
              <p>{t("educationCalendar.kindDistribution.subtitle")}</p>
            </div>
            {kindDonutTotal === 0 ? (
              <div className={styles.emptyCell}>
                Bu görünümde gösterilecek etkinlik yok.
              </div>
            ) : (
              <div className={styles.donutLayout}>
                <div className={styles.donutChart}>
                  <Chart
                    options={kindDonutOptions}
                    series={kindDonutSeries}
                    type="donut"
                    height={260}
                  />
                  <div className={styles.donutCenter}>
                    <span className={styles.donutCenterValue}>
                      {kindDonutTotal}
                    </span>
                    <span className={styles.donutCenterLabel}>
                      Toplam Etkinlik
                    </span>
                  </div>
                </div>
                <ul className={styles.donutLegend}>
                  {donutLegend.map((l) => {
                    const pct =
                      kindDonutTotal > 0
                        ? Math.round((l.value / kindDonutTotal) * 100)
                        : 0;
                    return (
                      <li key={l.label} className={styles.donutLegendItem}>
                        <span
                          className={styles.donutLegendDot}
                          style={{ background: l.color }}
                        />
                        <div className={styles.donutLegendMeta}>
                          <span className={styles.donutLegendLabel}>
                            {l.label}
                          </span>
                          <div className={styles.donutLegendValueRow}>
                            <span className={styles.donutLegendValue}>
                              {l.value}
                            </span>
                            <span className={styles.donutLegendPct}>%{pct}</span>
                          </div>
                          <div className={styles.donutLegendBarTrack}>
                            <span
                              className={styles.donutLegendBarFill}
                              style={{
                                width: `${pct}%`,
                                background: l.color,
                              }}
                            />
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EducationCalendarPage;
